import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Designation,
  DesignationCreateInput,
  DesignationListResponse,
  DesignationSuggestScopesInput,
  DesignationSuggestScopesResponse,
  DesignationUpdateInput,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase F1 — Designation CRUD hooks for the IA Settings → Designations
 * editor.
 *
 * The BE (Phase E2c) already exposes `/auth/designations` with full CRUD
 * + ceiling validation. These hooks are deliberately thin wrappers — the
 * editor sheet does optimistic-create-then-rollback because the BE can
 * still reject on `AZ_OVERRIDE_EXCEEDS_CEILING` and we want the user to
 * see the offending row, not a vanished optimistic stub.
 *
 * Cache key is `["designations"]` — the same list query underpins:
 *   - `DesignationsPage` table.
 *   - The role-picker dropdown that will land on the Members page in F1
 *     follow-up (replacing the existing hardcoded `UserRole` enum
 *     dropdown).
 * So invalidating once on every mutation keeps both surfaces in sync.
 */

export const DESIGNATIONS_QUERY_KEY = ["designations"] as const;

export function useDesignations() {
  return useQuery<DesignationListResponse>({
    queryKey: DESIGNATIONS_QUERY_KEY,
    queryFn: () => api.get<DesignationListResponse>("/auth/designations"),
    // The list is read-heavy (every Members page load also hits it) and
    // changes infrequently. 60s stale window keeps the typical
    // open-the-sheet → save → close flow from hammering the BE.
    staleTime: 60 * 1000,
  });
}

export function useCreateDesignation() {
  const qc = useQueryClient();
  return useMutation<Designation, Error, DesignationCreateInput>({
    mutationKey: ["designations", "create"],
    mutationFn: (body) => api.post<Designation>("/auth/designations", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DESIGNATIONS_QUERY_KEY });
    },
  });
}

export interface UseUpdateDesignationVars {
  id: string;
  body: DesignationUpdateInput;
}

export function useUpdateDesignation() {
  const qc = useQueryClient();
  return useMutation<Designation, Error, UseUpdateDesignationVars>({
    mutationKey: ["designations", "update"],
    mutationFn: ({ id, body }) => api.put<Designation>(`/auth/designations/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DESIGNATIONS_QUERY_KEY });
    },
  });
}

export function useDeleteDesignation() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationKey: ["designations", "delete"],
    mutationFn: (id) => api.delete<{ ok: boolean }>(`/auth/designations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DESIGNATIONS_QUERY_KEY });
    },
  });
}

/**
 * Phase F2 — AI-suggested permission overrides.
 *
 * Wraps `POST /auth/designations/suggest-scopes`. Deliberately NOT a
 * `useQuery` — the editor only fires this on explicit user intent
 * ("Suggest scopes" button click), not on mount, so the AI bill doesn't
 * climb every time someone opens the sheet. Caching is also intentionally
 * disabled (no `queryKey`) because suggestions evolve as policy changes
 * and a stale chip pre-fill would mislead more than it helps.
 *
 * Failure modes:
 *   - HTTP 503 + `Retry-After: 60` → all AI providers exhausted; the
 *     UI surfaces "AI suggestion unavailable, retry in a moment" + a
 *     manual-retry CTA. The editor stays usable; the admin proceeds
 *     by hand.
 *   - HTTP 200 + `parse_error` non-null → AI returned non-JSON. Same
 *     UX as 503 but no retry encouragement (the model is having an
 *     off-day; bouncing won't help). The admin proceeds by hand.
 *   - HTTP 403 → user lost `manage_roles` mid-session (extremely rare,
 *     possible if an SA revokes mid-session). Surfaced as a generic
 *     "permission required" toast.
 */
export function useSuggestDesignationScopes() {
  return useMutation<DesignationSuggestScopesResponse, Error, DesignationSuggestScopesInput>({
    mutationKey: ["designations", "suggest-scopes"],
    mutationFn: (body) =>
      api.post<DesignationSuggestScopesResponse>("/auth/designations/suggest-scopes", body),
  });
}
